import { Shield, Eye, Sparkles, Zap, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ApprovalPreset } from './useWelcomeState';

interface Props {
  value: ApprovalPreset | '';
  onChange: (preset: ApprovalPreset) => void;
}

interface PresetOption {
  value: ApprovalPreset;
  label: string;
  icon: typeof Shield;
  tagline: string;
  bullets: string[];
  recommended?: boolean;
}

const PRESETS: PresetOption[] = [
  {
    value: 'full_manual',
    label: 'Full Manual',
    icon: Shield,
    tagline: 'Dippy drafts everything, you approve every action.',
    bullets: [
      'Dippy creates draft emails for your review',
      'No emails are sent without your approval',
      'All product listings stay as drafts until you publish',
    ],
  },
  {
    value: 'suggest_only',
    label: 'Suggest Only',
    icon: Eye,
    tagline: 'Dippy researches and proposes — you decide.',
    bullets: [
      'Dippy finds suppliers and prepares recommendations',
      'You get a daily digest of proposals to review',
      'Nothing is published or sent without your OK',
    ],
  },
  {
    value: 'auto_with_review',
    label: 'Auto with Review',
    icon: Sparkles,
    recommended: true,
    tagline: 'Dippy acts on safe operations, flags risky ones.',
    bullets: [
      'Dippy sends template-based supplier emails automatically',
      'Product drafts publish after Dippy reviews them',
      'Risky actions (orders over $100, price changes) ask for approval',
    ],
  },
  {
    value: 'fully_autonomous',
    label: 'Fully Autonomous',
    icon: Zap,
    tagline: 'Dippy runs everything. You get notifications, not approvals.',
    bullets: [
      'Dippy places supplier orders up to your configured limit',
      'Dippy adjusts prices within your margin guardrails',
      'You get notified of every major action after the fact',
    ],
  },
];

export default function WelcomeStep2Autonomy({ value, onChange }: Props) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        How hands-on do you want to be with Dippy? You can change this later in the AI Agent settings.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PRESETS.map((preset) => {
          const isSelected = value === preset.value;
          const Icon = preset.icon;
          return (
            <button
              type="button"
              key={preset.value}
              onClick={() => onChange(preset.value)}
              data-testid={`preset-${preset.value}`}
              className={cn(
                'relative p-5 text-left rounded-xl bg-white shadow-sm border-2 transition-all w-full',
                isSelected
                  ? 'border-sage-500 bg-sage-50'
                  : 'border-stone-200 hover:border-sage-300'
              )}
            >
              {preset.recommended && (
                <span className="absolute -top-2 right-4 px-2 py-0.5 text-xs font-semibold bg-sage-600 text-white rounded-full">
                  Recommended
                </span>
              )}
              <div className="flex items-start gap-3 mb-3">
                <div
                  className={cn(
                    'shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                    isSelected ? 'bg-sage-600 text-white' : 'bg-stone-100 text-stone-600'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">{preset.label}</h3>
                  <p className="text-sm text-text-secondary">{preset.tagline}</p>
                </div>
              </div>
              <ul className="space-y-1.5 pl-1">
                {preset.bullets.map((bullet, i) => (
                  <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                    <Check className={cn(
                      'w-4 h-4 shrink-0 mt-0.5',
                      isSelected ? 'text-sage-600' : 'text-stone-400'
                    )} />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
    </div>
  );
}
